import { useAppStore } from '@runtime/core/store/appStore'
import { canAccessPosterTheme, setPosterThemeAccessToken } from '@runtime/core/theme/posterAccess'
import { requestPosterThemeAccess } from '@runtime/core/theme/posterThemeAccessApi'
import {
  resolvePendingPosterThemeFromIndex,
  type PendingPosterTheme,
} from '@runtime/core/theme/pendingPosterTheme'
import {
  DEFAULT_POSTER_THEME_ID,
  loadThemesIndex,
  precacheThemeAssets,
  resolveTheme,
  type ResolvedPosterThemeConfig,
} from '@runtime/core/theme/posterThemes'
import { hasCachedThemeAssets, isOffline } from '@runtime/core/theme/themeEngineCache'
import type { HomeSelection } from './homeArrival'

export type HomePosterResult =
  | { status: 'ready'; cfg: ResolvedPosterThemeConfig; pending?: PendingPosterTheme }
  | { status: 'unknown' | 'inaccessible' | 'unavailable' | 'offline'; requested: string }
  | { status: 'cancelled' }

export async function resolveHomePoster(
  selection: HomeSelection | null,
  storedId: string,
  isCurrent: () => boolean,
): Promise<HomePosterResult> {
  const requested = selection?.value || storedId || DEFAULT_POSTER_THEME_ID
  const unavailable = (): HomePosterResult => ({
    status: isOffline() ? 'offline' : 'unavailable',
    requested,
  })
  try {
    let id = storedId || DEFAULT_POSTER_THEME_ID
    let pending: PendingPosterTheme | undefined
    if (selection && selection.value !== DEFAULT_POSTER_THEME_ID) {
      const index = await loadThemesIndex({ strict: true })
      if (!isCurrent()) return { status: 'cancelled' }
      const match = resolvePendingPosterThemeFromIndex(selection.value, index)
      if (!match) return { status: 'unknown', requested }
      id = match.themeId
      pending = match
    } else if (selection) id = DEFAULT_POSTER_THEME_ID

    if (!isCurrent()) return { status: 'cancelled' }
    const { user, authToken } = useAppStore.getState()
    if (!canAccessPosterTheme(id, user)) {
      const accessToken = await requestPosterThemeAccess({
        themeId: id,
        authToken,
        publicSlug: selection?.value,
      }).catch(() => null)
      if (!isCurrent()) return { status: 'cancelled' }
      if (!accessToken) return { status: 'inaccessible', requested }
      setPosterThemeAccessToken(id, accessToken)
    }
    const cfg = await resolveTheme(id)
    if (!isCurrent()) return { status: 'cancelled' }
    if (!cfg || cfg.id !== id) return unavailable()
    if (id !== DEFAULT_POSTER_THEME_ID && isOffline() && !(await hasCachedThemeAssets(id)))
      return unavailable()
    await precacheThemeAssets(id, cfg)
    if (!isCurrent()) return { status: 'cancelled' }
    return { status: 'ready', cfg, pending }
  } catch {
    return unavailable()
  }
}
